import { Component, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { QaiLoadingComponent, QaiTableComponent, type HeaderConfig, type TableRow, QaiSearchableSelectComponent, QaiSearchableSelectOption, QaiIconComponent, QaiDatepickerComponent } from '@qai/ui';
import { TranslationService } from 'src/app/services/translation.service';
import { TranslocoModule, TranslocoService, TRANSLOCO_SCOPE } from '@jsverse/transloco';
import { IAssignmentViewStatsResponse } from 'src/app/interfaces/reportingV2/iassignment-view-stats-response';
import { formatDurationSeconds } from 'src/app/utils/date-parser.util';
import { ReportingDashboardService } from 'src/app/services/reporting-dashboard/reporting-dashboard.service';
import { IAssignmentViewStatsRequest } from 'src/app/interfaces/reportingV2/iassignment-view-stats-request';
import { ReportingLocalStorageUtil } from 'src/app/utils/reporting-localstorage.util';
import { ActivatedRoute, Router } from '@angular/router';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { LocaleService } from 'src/app/services/locale.service';
import { CsvExportService } from 'src/app/services/general/csv-export.service';
import { AppConstants } from 'src/app/app-constants';
import { withDemoModule } from 'src/app/utils/demo-mode/demo-mode-context.util';

@Component({
  selector: 'app-certification-attempts',
  templateUrl: './certification-attempts.component.html',
  styleUrls: ['./certification-attempts.component.scss'],
  standalone: true,
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'reportingV2',
    },
  ],
  imports: [
    CommonModule,
    QaiTableComponent,
    QaiSearchableSelectComponent,
    TranslocoModule,
    QaiLoadingComponent,
    QaiIconComponent,
    QaiDatepickerComponent
],
})
export class CertificationAttemptsComponent implements OnInit, OnChanges {
  @Input() selectedTeams: string[] = [];
  isTranslationLoaded = false;
  headers: HeaderConfig[] = [];
  originalData: TableRow[] = [];
  tableData: TableRow[] = [];
  certificationAttempts: IAssignmentViewStatsResponse[] = [];

  // Filter options
  userOptions: QaiSearchableSelectOption[] = [];
  simulationOptions: QaiSearchableSelectOption[] = [];

  // Selected filter values - now arrays for multiselect
  selectedUsers: string[] | string = [];
  selectedSimulations: string[] | string = [];
  userClearOption = { enable: true, label: 'Clear' };
  simulationClearOption = { enable: true, label: 'Clear' };
  scopedOptionsSelectionModeUsers = {
    enable: true,
    selectAllOption: { value: 'selectAll', label: 'Select all' },
    deselectAllOption: { value: 'deselectAll', label: 'Deselect all' },
  };
  scopedOptionsSelectionModeSimulations = {
    enable: true,
    selectAllOption: { value: 'selectAll', label: 'Select all' },
    deselectAllOption: { value: 'deselectAll', label: 'Deselect all' },
  };
  defaultSortColumn="";
  defaultSortDirection: 'desc' = 'desc';
  pageSize = 10;
  currentPage = 1;
  showPagination = true;
  isLoading = false;
  certificationId: string | null = null;
  accountUuid: string | null = null;

  startDate: string | null = null;
  endDate: string | null = null;
  minStartDate: string = '';
  maxStartDate: string = '';
  minEndDate: string = '';
  maxEndDate: string = '';

  simulationNameColumnHeader: string = '';

  @Input() assignmentName: string = '';

  constructor(
    private translationService: TranslationService,
    private translocoService: TranslocoService,
    private route: ActivatedRoute,
    private reportingDashboardService: ReportingDashboardService,
    private amplitudeService: AmplitudeService,
    private localeService: LocaleService,
    private csvExportService: CsvExportService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.certificationId = this.route.snapshot.paramMap.get('id');
    this.accountUuid = ReportingLocalStorageUtil.getStoredAccountUuid();
    this.translationService.loadScopedTranslation('reportingV2').then(() => {
      this.isTranslationLoaded = true;
      this.scopedOptionsSelectionModeUsers.selectAllOption!.label =
        this.translocoService.translate('SELECT_ALL', {}, 'reportingV2');
      this.scopedOptionsSelectionModeUsers.deselectAllOption!.label =
        this.translocoService.translate('DESELECT_ALL', {}, 'reportingV2');
      this.scopedOptionsSelectionModeSimulations.selectAllOption!.label =
        this.translocoService.translate('SELECT_ALL', {}, 'reportingV2');
      this.scopedOptionsSelectionModeSimulations.deselectAllOption!.label =
        this.translocoService.translate('DESELECT_ALL', {}, 'reportingV2');
      this.userClearOption.label = this.translocoService.translate('CLEAR', {}, 'reportingV2');
      this.simulationClearOption.label = this.translocoService.translate('CLEAR', {}, 'reportingV2');
      this.initializeHeaders();
      this.defaultSortColumn = this.headers[2].name;
      this.getCertificationAttempts();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedTeams'] && this.isTranslationLoaded) {
      this.getCertificationAttempts();
    }
  }

  private initializeHeaders() {
    this.simulationNameColumnHeader = this.translocoService.translate('SIMULATION', {}, 'reportingV2');
    this.headers = [
      { name: this.translocoService.translate('USER_NAME', {}, 'reportingV2'), sortable: true, columnWidth: '8%', clickable: true },
      { name: this.simulationNameColumnHeader, sortable: true, columnWidth: '13%', clickable: true },
      { name: this.translocoService.translate('DATE_OF_SESSION', {}, 'reportingV2'), sortable: true, columnWidth: '12%' },
      { name: this.translocoService.translate('DURATION_OF_ATTEMPT', {}, 'reportingV2'), sortable: true, columnWidth: '12%' },
      { name: this.translocoService.translate('OVERALL_SCORE', {}, 'reportingV2'), sortable: true, columnWidth: '11%' },
      { name: this.translocoService.translate('MESSAGING_SCORE_LABEL', {}, 'reportingV2'), sortable: true, columnWidth: '15%'},
      { name: this.translocoService.translate('METHODOLOGY_SCORE', {}, 'reportingV2'), sortable: true, columnWidth: '15%' },
      { name: this.translocoService.translate('SKILLS_SCORE', {}, 'reportingV2'), sortable: true, columnWidth: '14%' }
    ];
  }

  private transformApiDataToTableRows(
    apiData: IAssignmentViewStatsResponse[]
  ): TableRow[] {
    return apiData.map((attempt) => this.createAttemptTableRow(attempt));
  }

  private createAttemptTableRow(attempt: IAssignmentViewStatsResponse): TableRow {
    const row = {
      id: attempt.uuid,
      email: attempt.userEmail,
      teams: attempt.userTeams,
      userUuid: attempt.userUuid,
      simulationUuid: attempt.simulationUuid,
      [this.headers[0].name]: { type: 'text', value: attempt.userName },
      [this.headers[1].name]: { type: 'text', value: attempt.simulationName },
      [this.headers[2].name]: { type: 'text', value: this.localeService.formatSessionDateTime(attempt.sessionDate) },
      [this.headers[3].name]: {
        type: 'text',
        value: this.translateDuration(formatDurationSeconds(attempt.durationSeconds)),
      },
      [this.headers[4].name]: {
        type: 'text',
        value: this.localeService.formatScoreValue(attempt.overallScore?.toString()),
      },
      [this.headers[5].name]: { type: 'text', value: this.localeService.formatScoreValue(attempt.messagingScore) },
      [this.headers[6].name]: { type: 'text', value: this.localeService.formatScoreValue(attempt.methodologyScore) },
      [this.headers[7].name]: { type: 'text', value: this.localeService.formatScoreValue(attempt.skillsScore) }
    } as const;

    return { ...row } as unknown as TableRow;
  }

  pageChanged(event: { page: number }): void {
    this.currentPage = event.page;
  }

  onDateChange(controlName: 'startDate' | 'endDate', value: string | null): void {
    this.amplitudeService.trackEvent(AmplitudeTrackingEvents.REPORTINGDASHBOARD_CLICK_DATE_FILTER);
    const dateValue = value ?? '';

    if (controlName === 'startDate') {
      this.startDate = dateValue;
      this.handleStartDateChange(dateValue);
    }

    if (controlName === 'endDate') {
      this.endDate = dateValue;
      this.validateDateRange();
    }

    this.applyFilters();
  }

  private handleStartDateChange(startDateValue: string): void {
    if (startDateValue) {
      this.minEndDate = startDateValue;

      if (this.endDate) {
        this.validateDateRange();
      }
    } else {
      this.minEndDate = this.minStartDate;
    }
  }

  private validateDateRange(): void {
    if (this.startDate && this.endDate) {
      const start = new Date(this.startDate);
      start.setHours(0, 0, 0, 0);

      const end = new Date(this.endDate);
      end.setHours(23, 59, 59, 999);

      if (end < start) {
        console.warn('Invalid date range: End date is before start date');
        this.endDate = '';
      }
    }
  }

  private initializeFilters() {
    // Extract unique user names - only from text cells
    const uniqueUsers = [
      ...new Set(
        this.originalData
          .map((row) => row[this.headers[0].name])
          .filter((cell) => cell.type === 'text')
          .map((cell) => (cell as any).value)
      ),
    ];
    this.userOptions = uniqueUsers.map((user) => ({
      label: user,
      value: user,
    }));

    // Extract unique simulations - only from text cells
    const uniqueSimulations = [
      ...new Set(
        this.originalData
          .map((row) => row[this.headers[1].name])
          .filter((cell) => cell.type === 'text')
          .map((cell) => (cell as any).value)
      ),
    ];
    this.simulationOptions = uniqueSimulations.map((simulation) => ({
      label: simulation,
      value: simulation,
    }));

    this.initializeDateRange();
  }

  private initializeDateRange(): void {
    const dateCells = this.originalData
      .map(row => {
        const cell = row[this.headers[2].name];
        return (cell as any)?.value;
      })
      .filter(dateStr => !!dateStr)
      .map(dateStr => new Date(dateStr))
      .filter(d => !isNaN(d.getTime()));

    if (!dateCells.length) return;

    const minDate = new Date(Math.min(...dateCells.map(d => d.getTime())));
    const maxDate = new Date(Math.max(...dateCells.map(d => d.getTime())));

    const toLocalDateString = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    this.minStartDate = toLocalDateString(minDate);
    this.maxStartDate = toLocalDateString(maxDate);
    this.minEndDate = toLocalDateString(minDate);
    this.maxEndDate = toLocalDateString(maxDate);
  }


  onUserFilterChange(selectedValue: string | string[]) {
    this.amplitudeService.trackEvent(AmplitudeTrackingEvents.REPORTINGDASHBOARD_CLICK_USER_NAME_FILTER);
    // Handle multiselect - always expect an array
    this.selectedUsers = Array.isArray(selectedValue)
      ? selectedValue
      : [selectedValue];
    this.applyFilters();
  }

  onSimulationFilterChange(selectedValue: string | string[]) {
    // Handle multiselect - always expect an array
    this.selectedSimulations = Array.isArray(selectedValue)
      ? selectedValue
      : [selectedValue];
    this.applyFilters();
  }

  onSimulationSelected(simulationName: string) {
    this.amplitudeService.trackEvent(
      AmplitudeTrackingEvents.REPORTINGDASHBOARD_CLICK_SIMULATION_FILTER,
      { simulation_name: simulationName }
    );
  }

  private applyFilters() {
    let filteredData = [...this.originalData];

    // Apply user filter
    if (this.selectedUsers.length > 0) {
      filteredData = filteredData.filter((row) => {
        const cell = row[this.headers[0].name];
        if (cell?.type === 'text') {
          return this.selectedUsers.includes((cell as any).value);
        }
        return false;
      });
    }

    // Apply simulation filter
    if (this.selectedSimulations.length > 0) {
      filteredData = filteredData.filter((row) => {
        const cell = row[this.headers[1].name];
        if (cell?.type === 'text') {
          return this.selectedSimulations.includes((cell as any).value);
        }
        return false;
      });
    }

    if (this.startDate || this.endDate) {
      filteredData = filteredData.filter((row) => {
        const cell = row[this.headers[2].name];
        const rawDate = (cell as any)?.value;
        const rowDate = new Date(rawDate);

        if (isNaN(rowDate.getTime())) return false;

        const rowOnly = new Date(rowDate);
        rowOnly.setHours(0, 0, 0, 0);

        const start = this.startDate ? new Date(this.startDate) : null;
        if (start) start.setHours(0, 0, 0, 0);

        const end = this.endDate ? new Date(this.endDate) : null;
        if (end) end.setHours(23, 59, 59, 999);

        return (
          (!start || rowOnly >= start) &&
          (!end || rowOnly <= end)
        );
      });
    }

    this.tableData = filteredData;
    this.currentPage = 1;
  }

  clearFilters() {
    this.selectedUsers = [];
    this.selectedSimulations = [];
    this.applyFilters();
  }

  protected onDownloadIconClick(): void {
    const fileName = this.csvExportService.getCsvFileName(this.assignmentName, 'attempt_data');
    const sessionIdHeader = this.translocoService.translate('SESSION_ID', {}, 'reportingV2');
    const nameHeader = this.headers[0]?.name;
    const emailHeader = this.translocoService.translate('EMAIL_LABEL', {}, 'reportingV2');
    const teamsHeader = this.translocoService.translate('TEAM_LABEL', {}, 'reportingV2');
    const uiSimulationHeader = this.headers[1]?.name;
    const csvSimulationHeader = this.translocoService.translate('SIMULATION_NAME', {}, 'reportingV2');

    const csvData = this.originalData.map(row => {
      const csvRow: Record<string, unknown> = {
        [sessionIdHeader]: row.id,
        [nameHeader]: row[nameHeader],
        [emailHeader]: row['email'],
        [teamsHeader]: row['teams']
      };

      this.headers.forEach(header => {
        if (header.name === nameHeader) return;
        if (header.name === uiSimulationHeader) {
          csvRow[csvSimulationHeader] = row[header.name];
        } else {
          csvRow[header.name] = row[header.name];
        }
      });

      return csvRow;
    });

    const headerNames = [
      sessionIdHeader,
      nameHeader,
      emailHeader,
      teamsHeader,
      ...this.headers.map(h =>
        h.name === uiSimulationHeader ? csvSimulationHeader : h.name
      )
      .filter(n => n !== nameHeader)
    ];

    this.csvExportService.exportToCsv(csvData, fileName, headerNames);
  }

  onCellClick(event: { rowData: TableRow; columnName: string; event: Event }) {
    if(event.columnName === this.simulationNameColumnHeader) {
      const simulation = event.rowData[this.simulationNameColumnHeader] as Record<string, any>;
      const simulationValue = String(simulation?.['value'] ?? '');
      this.amplitudeService.trackEvent(AmplitudeTrackingEvents.ASSIGNMENTREPORT_CLICK_ATTEMPTTABLE_SIMNAME, {
        simulation_name: simulationValue,
      });
      const row = event.rowData as Record<string, any>;
      const simulationUuid = String(row['simulationUuid'] ?? '');
      this.navigateToSimulationDetail(simulationUuid, simulationValue);
    }
    else {
      const row = event.rowData as Record<string, any>;
      const userUuid = row['userUuid'] ?? '';
      this.navigateToUserDetail(String(userUuid));
    }
  }

  navigateToUserDetail(userId: string | number) {
    this.router.navigate(['/reporting_dashboard/user', userId], {
      queryParams: { assignmentUuid: this.certificationId },
    });
  }

  navigateToSimulationDetail(simulationId: string | number, simulationName: string) {
    this.router.navigate(['/reporting_dashboard/simulation', simulationId], {
      state: {
        simulationName: simulationName,
        assignmentName: this.assignmentName,
        selectedTeamUuids: this.selectedTeams ?? [],
      },
      queryParams: { moduleAssignmentUuid: this.certificationId },
    });
  }

  private translateDuration(durationString: string): string {
    const minText = this.translocoService.translate('MIN', {}, 'reportingV2');
    const secText = this.translocoService.translate('SEC', {}, 'reportingV2');
    return durationString.replace('min', minText).replace('sec', secText);
  }

  private getCertificationAttempts(): void {
    const accountUuid = this.accountUuid;
    const moduleAssignmentUuid = this.certificationId;
    const teamUuids: string[] = this.selectedTeams.includes('all') ? [] : this.selectedTeams;
    this.isLoading = true;

    if (!accountUuid || !moduleAssignmentUuid) {
      console.warn('Missing required data for certification attempts', accountUuid, moduleAssignmentUuid);
      this.isLoading = false;
      return;
    }

    const payload: IAssignmentViewStatsRequest = {
      accountUuid: accountUuid,
      teamUuids: teamUuids,
      moduleAssignmentUuid: moduleAssignmentUuid,
    };

    this.reportingDashboardService.getAssignmentViewStats(payload, withDemoModule(AppConstants.NEW_REPORTING_UI)).subscribe({
      next: (attempts) => {
        this.certificationAttempts = attempts;
        this.originalData = this.transformApiDataToTableRows(attempts);
        this.tableData = [...this.originalData];
        this.initializeFilters();
        this.applyFilters();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading certification attempts:', error);
        this.certificationAttempts = [];
        this.originalData = [];
        this.tableData = [];
        this.isLoading = false;
      }
    });
  }
}